import React, { useMemo } from 'react'
import { StyleSheet, ScrollView } from 'react-native'
import { useMutation } from 'convex/react'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { api } from '@ppwr/convex/_generated/api'
import { DS } from '@/design-system'
import { CardLiveGame } from '@/screens/play-screen/Sections/TodayLiveGames/CardLiveGame'
import { useRouter } from 'expo-router'
import { useStore } from '@/models'
import { DEFAULT_MAX_PLAYERS } from '@/constants'
import { useNotificationSheet } from '@/components/Notifications/useNotificationSheet'
import NotificationCheatSheet from '@/components/Notifications/NotificationsSheet'
import { resolveGameStatus, CARD_VISIBILITY_WINDOW_MS, JOIN_WINDOW_MS } from './gameStatus'
import { usePublicSchedules } from './usePublicSchedules'
import { GamesEmptyMessage } from '../play-screen/Sections/YourGames'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'

type CreatedGame = {
  gameId: string
  name: string
  state: string
  /** Current number of players (from Convex gameMetadata); for seats [playerCount, maxPlayers] */
  playerCount?: number
  leftPlayerUserIds?: string[]
  tableConfigIndex?: number
  tableNumber?: number
  tableType?: 'beginner' | 'main'
  turnDuration?: number
  bigBlind?: number
  smallBlind?: number
  maxPlayers?: number
}

/**
 * Build advanced tables from created games (including virtual tables).
 * For unified schedules, each table has its own config (turnDuration, blinds) and tableConfigIndex/tableNumber for join.
 */
function createAdvancedTables(
  createdGames: CreatedGame[],
  schedule: {
    name?: string
    tableConfigs?: Array<{
      turnDuration: number
      bigBlind?: number
      smallBlind?: number
      maxPlayers?: number
      difficulty?: string
      numberOfTables?: number
      minPlayers?: number
    }>
  },
  scheduleId: string,
  defaultTableType: 'beginner' | 'main'
) {
  const firstConfig = schedule.tableConfigs?.[0]
  const schedulePrefix = schedule.name ? `${schedule.name} - ` : ''
  /** REMOVE - Legacy Convex titles used a literal " Table" / " Table N" suffix before canonical names. */
  const stripPublicDailyTableSuffix = (title: string) =>
    title.replace(/\sTable (\d+)$/u, ' $1').replace(/\sTable$/u, '')
  return createdGames
    .map((game) => {
      const tableType = game.tableType ?? defaultTableType
      const turnDuration = game.turnDuration ?? firstConfig?.turnDuration ?? 30
      const bigBlind = game.bigBlind ?? firstConfig?.bigBlind ?? 100
      const smallBlind = game.smallBlind ?? firstConfig?.smallBlind ?? bigBlind / 2
      const maxPlayers = game.maxPlayers ?? firstConfig?.maxPlayers ?? DEFAULT_MAX_PLAYERS
      const playerCount = Math.min(game.playerCount ?? 0, maxPlayers)
      const stripped =
        schedulePrefix && game.name.startsWith(schedulePrefix) ? game.name.slice(schedulePrefix.length) : game.name
      const tableName = stripPublicDailyTableSuffix(stripped)
      return {
        tableName,
        channelName: game.gameId || '',
        actionTime: turnDuration,
        blinds: [smallBlind, bigBlind] as [number, number],
        seats: [playerCount, maxPlayers] as [number, number],
        isFull: game.state === 'finished',
        isLocked: game.state === 'finished',
        hasLeftGame: false,
        scheduleId,
        tableType,
        tableConfigIndex: game.tableConfigIndex,
        tableNumber: game.tableNumber,
        isVirtual: !game.gameId || game.gameId === ''
      }
    })
    .sort((a, b) => a.tableName.localeCompare(b.tableName))
}

/**
 * Community Games Section Component
 * Displays upcoming public daily games using CardLiveGame design
 */

export const CommunityGamesSection = () => {
  const router = useRouter()
  const {
    session: { user }
  } = useStore()

  // Mutations: join existing game by id, or join/create via schedule (ending games is done by backend cron)
  const joinGameMutation = useMutation(api.games.mutations.joinGame)
  const joinScheduledTable = useMutation(api.gameSchedules.mutations.joinScheduledTable)

  const { bottomNotificationSheetModalRef, handleOpenNotificationSheet } = useNotificationSheet()

  const { upcomingGames } = usePublicSchedules()

  // Transform data for CardLiveGame component, then merge cards at the same time into one
  const gameCards = useMemo(() => {
    if (!upcomingGames || upcomingGames.length === 0) {
      return []
    }

    const now = Date.now()
    // Don't show cards more than CARD_VISIBILITY_WINDOW_MS in the past
    const withinRange = upcomingGames.filter((item) => item.nextOccurrence >= now - CARD_VISIBILITY_WINDOW_MS)

    const timeKey = (item: (typeof upcomingGames)[0]) =>
      `${item.schedule._id}-${item.schedule.scheduledStartTime}-${item.nextOccurrence}`

    const cards = withinRange.map((item) => {
      const { type, canJoin } = resolveGameStatus(item.nextOccurrence, item.createdGames)

      // Total players across tables: sum playerCount per unique gameId (same game can appear in multiple slots)
      const nowPlaying = (() => {
        const byGameId = new Map<string, number>()
        for (const g of item.createdGames ?? []) {
          if (g.gameId && g.playerCount !== undefined) byGameId.set(g.gameId, g.playerCount)
        }
        return Array.from(byGameId.values()).reduce((s, n) => s + n, 0)
      })()
      const hasTableConfigs = item.schedule.tableConfigs && item.schedule.tableConfigs.length > 0
      const defaultTableType: 'beginner' | 'main' =
        item.schedule.tableConfigs?.[0]?.difficulty === 'BEGINNER' ? 'beginner' : 'main'
      const createdGamesTyped = (item.createdGames ?? []) as CreatedGame[]
      const beginnerGames = hasTableConfigs
        ? createdGamesTyped.filter((g) => g.tableType === 'beginner')
        : item.schedule.tableConfigs?.[0]?.difficulty === 'BEGINNER'
          ? createdGamesTyped
          : []
      const mainGames = hasTableConfigs
        ? createdGamesTyped.filter((g) => g.tableType === 'main')
        : item.schedule.tableConfigs?.[0]?.difficulty !== 'BEGINNER'
          ? createdGamesTyped
          : []

      const advancedTables =
        mainGames.length > 0 ? createAdvancedTables(mainGames, item.schedule, item.schedule._id, 'main') : undefined

      const nBeginnerTables = hasTableConfigs
        ? beginnerGames.length
        : item.schedule.tableConfigs?.[0]?.difficulty === 'BEGINNER'
          ? (item.schedule.tableConfigs?.[0]?.numberOfTables ?? 0)
          : 0
      const nAdvancedTables = hasTableConfigs
        ? mainGames.length
        : item.schedule.tableConfigs?.[0]?.difficulty !== 'BEGINNER'
          ? (item.schedule.tableConfigs?.[0]?.numberOfTables ?? 0)
          : 0

      // Lock (hide join buttons) when finished or when not in join window (e.g. upcoming)
      const shouldLock = !canJoin

      const nextDate = new Date(item.nextOccurrence)

      const gameIds = (item.createdGames ?? [])
        .map((g) => g.gameId)
        .filter((id): id is string => Boolean(id)) as Id<'games'>[]

      return {
        timeKey: timeKey(item),
        key: `${item.schedule._id}-${item.nextOccurrence}`,
        type,
        time: nextDate,
        nowPlaying,
        nBeginnerTables,
        nAdvancedTables,
        minPlayersN: (() => {
          const mins = (item.schedule.tableConfigs ?? []).map((c) => c.minPlayers).filter((n): n is number => n != null)
          return mins.length ? Math.min(...mins) : 2
        })(),
        maxPlayersN: (() => {
          const maxes = (item.schedule.tableConfigs ?? [])
            .map((c) => c.maxPlayers)
            .filter((n): n is number => n != null)
          if (maxes.length === 0) return GAME_CONSTANTS.DEFAULT_MAX_PLAYERS
          const minM = Math.min(...maxes)
          const maxM = Math.max(...maxes)
          return minM !== maxM ? ([minM, maxM] as [number, number]) : minM
        })(),
        multipleBlindOptions: false,
        isLocked: shouldLock,
        isFull: false,
        advancedTables: advancedTables ?? [],
        scheduleId: item.schedule._id,
        tableType: defaultTableType,
        canJoin,
        gameIds,
        winners: item.winnerNames?.length ? item.winnerNames : undefined,
        onBeginnerPress:
          nBeginnerTables > 0
            ? async () => {
                if (!user) return
                if (!canJoin) return

                const userId = user.auth0Id?.toString() || (typeof user.auth0Id === 'string' ? user.auth0Id : '')
                const mongoUserId = user._id?.toString()
                if (!userId || !mongoUserId) return

                const userName = user.name || 'Player'

                // 1) Try to join an existing beginner game first (from createdGames)
                const joinableBeginnerGames = beginnerGames.filter(
                  (g) =>
                    g.gameId &&
                    (g.state === 'pregame' || g.state === 'playing') &&
                    !(g.leftPlayerUserIds?.includes(userId) ?? false)
                )
                for (const game of joinableBeginnerGames) {
                  try {
                    await joinGameMutation({
                      gameId: game.gameId as Id<'games'>,
                      userId,
                      mongoUserId,
                      name: userName
                    })
                    router.replace({
                      pathname: '/game/[gameId]',
                      params: { gameId: game.gameId }
                    } as any)
                    return
                  } catch {
                    // game may be full or errored, try next
                  }
                }

                // 2) No existing game had space: create/find via schedule (table 1..4)
                const maxBeginnerTables = hasTableConfigs ? 4 : 1
                for (let tableNumber = 1; tableNumber <= maxBeginnerTables; tableNumber++) {
                  try {
                    const result = await joinScheduledTable({
                      scheduleId: item.schedule._id,
                      userId,
                      userName,
                      mongoUserId,
                      tableType: 'beginner',
                      ...(hasTableConfigs && { tableConfigIndex: 0, tableNumber })
                    })
                    await joinGameMutation({
                      gameId: result.gameId,
                      userId,
                      mongoUserId,
                      name: userName
                    })
                    router.replace({
                      pathname: '/game/[gameId]',
                      params: { gameId: result.gameId }
                    } as any)
                    return
                  } catch {
                    // table may be full, try next
                  }
                }
              }
            : undefined,
        onReminderPress: () => {
          const dayOfWeek = nextDate.getUTCDay()
          handleOpenNotificationSheet(dayOfWeek)
        }
      }
    })

    // Merge cards at the same time into one card per time slot
    const byTime = new Map<string, (typeof cards)[0][]>()
    for (const card of cards) {
      const key = card.timeKey
      const list = byTime.get(key)
      if (list) list.push(card)
      else byTime.set(key, [card])
    }

    const merged = Array.from(byTime.values()).map((group) => {
      if (group.length === 1) return group[0]
      const first = group[0]
      const nBeginnerTables = group.reduce((s, c) => s + c.nBeginnerTables, 0)
      const nAdvancedTables = group.reduce((s, c) => s + c.nAdvancedTables, 0)
      const advancedTables = group.flatMap((c) => c.advancedTables)
      const nowPlaying = group.reduce((s, c) => s + c.nowPlaying, 0)
      const gameIds = group.flatMap((c) => c.gameIds ?? [])
      const winners = group.flatMap((c) => c.winners ?? [])
      const beginnerCard = group.find((c) => c.nBeginnerTables > 0)
      // Joinable if any card in the group is not locked and not full
      const isLocked = group.every((c) => c.isLocked)
      const isFull = group.every((c) => c.isFull)
      return {
        ...first,
        key: first.timeKey,
        nBeginnerTables,
        nAdvancedTables,
        advancedTables,
        nowPlaying,
        gameIds,
        winners,
        isLocked,
        isFull,
        onBeginnerPress: beginnerCard?.onBeginnerPress ?? first.onBeginnerPress,
        onReminderPress: first.onReminderPress
      }
    })

    return merged
  }, [upcomingGames, router, user, joinGameMutation, joinScheduledTable, handleOpenNotificationSheet])

  if (upcomingGames === undefined) {
    return <GamesEmptyMessage text="Loading community games..." />
  }

  if (gameCards.length === 0) {
    return <GamesEmptyMessage text="No upcoming community games scheduled" icon="Suite--Club" />
  }

  return (
    <DS.Frame gap="16" style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        {gameCards.map((card) => (
          <CardLiveGame
            key={card.key}
            type={card.type}
            time={card.time}
            nowPlaying={card.nowPlaying}
            nBeginnerTables={card.nBeginnerTables}
            nAdvancedTables={card.nAdvancedTables}
            minPlayersN={card.minPlayersN}
            maxPlayersN={card.maxPlayersN as number | [number, number] | undefined}
            multipleBlindOptions={card.multipleBlindOptions}
            isLocked={card.isLocked}
            isFull={card.isFull}
            advancedTables={card.advancedTables}
            onBeginnerPress={card.onBeginnerPress}
            onReminderPress={card.onReminderPress}
            scheduleId={card.scheduleId}
            tableType={card.tableType as 'beginner' | 'main' | undefined}
            winners={card.winners?.length ? card.winners : undefined}
          />
        ))}
      </ScrollView>
      <NotificationCheatSheet bottomNotificationSheetModalRef={bottomNotificationSheetModalRef} />
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: DS.spacing[16],
    width: '100%'
  },
  scrollContent: {
    paddingRight: DS.spacing[16],
    gap: DS.spacing[16]
  },
  title: {
    marginBottom: DS.spacing[12],
    paddingHorizontal: DS.spacing[16]
  }
})
